import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, ChevronDown, Sparkles, Shield, UserPlus, CreditCard, UserCircle, Play, EyeOff, Eye } from 'lucide-react';
import { useOnboarding } from '../contexts/OnboardingContext';
import { isAdmin } from '../utils/adminUtils';

interface UserDropdownProps {
  user: any;
  onLogout: () => void;
  isLoggingOut?: boolean;
}

export const UserDropdown: React.FC<UserDropdownProps> = ({ user, onLogout, isLoggingOut = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { startUnifiedOnboarding } = useOnboarding();
  const [isFeedbackWidgetHidden, setIsFeedbackWidgetHidden] = useState(false);

  // Check if user is admin using the utility function
  const userIsAdmin = isAdmin(user);

  // Load feedback widget visibility state
  useEffect(() => {
    const hidden = localStorage.getItem('vitly-feedback-widget-hidden');
    setIsFeedbackWidgetHidden(hidden === 'true');
  }, []);

  // Toggle feedback widget visibility
  const toggleFeedbackWidget = () => {
    const newHiddenState = !isFeedbackWidgetHidden;
    setIsFeedbackWidgetHidden(newHiddenState);
    localStorage.setItem('vitly-feedback-widget-hidden', String(newHiddenState));

    // Trigger a storage event to update the FloatingFeedbackButton component
    window.dispatchEvent(new Event('storage'));
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials for avatar
  const getInitials = () => {
    // Try multiple field combinations for reliability
    const firstName = user.firstName || user.first_name || user.user_metadata?.first_name || user.user_metadata?.firstName || '';
    const lastName = user.lastName || user.last_name || user.user_metadata?.last_name || user.user_metadata?.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    
    // Try full name field
    const fullName = user.name || user.full_name || '';
    if (fullName && fullName.trim()) {
      const names = fullName.split(' ').filter((n: string) => n.length > 0);
      if (names.length >= 2) {
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
      } else if (names.length === 1) {
        return names[0].substring(0, 2).toUpperCase();
      }
    }
    
    // Fallback to email
    if (user.email) {
      const emailName = user.email.split('@')[0];
      if (emailName.includes('.')) {
        const parts = emailName.split('.');
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return emailName.substring(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  // Get user's full name for display
  const getFullName = () => {
    // Try multiple field combinations for reliability
    const firstName = user.firstName || user.first_name || user.user_metadata?.first_name || user.user_metadata?.firstName || '';
    const lastName = user.lastName || user.last_name || user.user_metadata?.last_name || user.user_metadata?.lastName || '';
    
    if (firstName || lastName) {
      const name = `${firstName} ${lastName}`.trim();
      if (name) return name;
    }
    
    // Try full name field
    const fullName = user.name || user.full_name || '';
    if (fullName && fullName.trim()) {
      return fullName.trim();
    }
    
    // Fallback to email username
    if (user.email) {
      const emailName = user.email.split('@')[0];
      // Capitalize first letter
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return 'User';
  };
  
  // Get avatar URL with fallbacks
  const getAvatarUrl = () => {
    return user.avatarUrl || 
           user.avatar_url || 
           user.user_metadata?.avatar_url || 
           user.user_metadata?.avatarUrl || 
           user.profile_picture || 
           null;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-lg hover:bg-white/10 transition-all duration-200"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        {getAvatarUrl() ? (
          <img
            src={getAvatarUrl()}
            alt={getFullName()}
            className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
            onError={(e) => {
              // Hide image if it fails to load
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-sm font-semibold">
            {getInitials()}
          </div>
        )}
        <ChevronDown className={`w-4 h-4 text-white/90 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              {getAvatarUrl() ? (
                <img
                  src={getAvatarUrl()}
                  alt={getFullName()}
                  className="w-10 h-10 rounded-full object-cover"
                  onError={(e) => {
                    // Hide image if it fails to load
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-semibold">
                  {getInitials()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {getFullName()}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <Link
              to="/profile"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <User className="w-4 h-4" />
              <span>Your Profile</span>
            </Link>
            {!userIsAdmin && (
              <Link
                to="/invitations"
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <UserPlus className="w-4 h-4" />
                <span>Guest Invites</span>
              </Link>
            )}
            <Link
              to="/account"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <UserCircle className="w-4 h-4" />
              <span>Account Settings</span>
            </Link>
            <Link
              to="/account/subscription"
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <CreditCard className="w-4 h-4" />
              <span>Subscription</span>
            </Link>

            {/* Beta Dashboard for all users - hidden when beta is disabled */}
            {import.meta.env.VITE_BETA_DISABLED !== 'true' && (
              <Link
                to="/beta/dashboard"
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <Sparkles className="w-4 h-4" />
                <span>Beta Dashboard</span>
              </Link>
            )}

            {/* Onboarding Wizard */}
            <button
              onClick={() => {
                startUnifiedOnboarding('full');
                setIsOpen(false);
              }}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors w-full text-left"
            >
              <Play className="w-4 h-4" />
              <span>Get Started Guide</span>
            </button>

            {/* Toggle Feedback Widget */}
            <button
              onClick={toggleFeedbackWidget}
              className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors w-full text-left"
            >
              {isFeedbackWidgetHidden ? (
                <>
                  <Eye className="w-4 h-4" />
                  <span>Show Feedback Widget</span>
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span>Hide Feedback Widget</span>
                </>
              )}
            </button>
          </div>

          {/* Admin Section - Only shown to admins */}
          {userIsAdmin && (
            <div className="border-t border-gray-100 pt-2 pb-2">
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Admin</p>
              </div>
              
              <Link
                to="/admin"
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors"
                onClick={() => setIsOpen(false)}
              >
                <Shield className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Link>
            </div>
          )}

          {/* Logout */}
           <div className="border-t border-gray-100 pt-2">
             <button
               onClick={() => {
                 setIsOpen(false);
                 onLogout();
               }}
               disabled={isLoggingOut}
               className="flex items-center gap-3 px-4 py-2 w-full text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isLoggingOut ? (
                 <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
               ) : (
                 <LogOut className="w-4 h-4" />
               )}
               <span>{isLoggingOut ? 'Signing out...' : 'Sign out'}</span>
             </button>
           </div>
        </div>
      )}
    </div>
  );
};
